import { useEffect, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import { useUpdateProgramConfig } from '../../hooks';
import { PROGRAM_MODEL_LABELS, PROGRAM_MODEL_OPTIONS } from '../../model/labels';
import type { LoyaltyProgramConfigResponse, LoyaltyProgramModel } from '../../model/types';

interface ProgramModelDialogProps {
  open: boolean;
  config: LoyaltyProgramConfigResponse | null;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Choose the tenant's loyalty programme working model (gated by `loyalty.manage`). */
export function ProgramModelDialog({ open, config, onClose, onDone }: ProgramModelDialogProps) {
  const [model, setModel] = useState<LoyaltyProgramModel>('BONUS');
  const [active, setActive] = useState(true);
  const [delayDays, setDelayDays] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const update = useUpdateProgramConfig();

  useEffect(() => {
    if (open) {
      setModel(config?.model ?? 'BONUS');
      setActive(config?.active ?? true);
      setDelayDays(
        config?.bonusActivationDelayDays != null ? String(config.bonusActivationDelayDays) : '0',
      );
      setError(null);
    }
  }, [open, config]);

  const delayNum = delayDays.trim() ? Number(delayDays) : 0;
  const validDelay = Number.isInteger(delayNum) && delayNum >= 0;

  const submit = () => {
    setError(null);
    update.mutate(
      {
        model,
        active,
        bonusActivationDelayDays: model === 'BONUS' ? delayNum : undefined,
      },
      {
        onSuccess: () => {
          onDone('Модель программы сохранена.');
          onClose();
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Модель программы</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            label="Модель"
            value={model}
            onChange={(e) => setModel(e.target.value as LoyaltyProgramModel)}
            fullWidth
            helperText="Бонусы — начисление баллов; Скидка — % по уровню; Выключена — программа отключена."
          >
            {PROGRAM_MODEL_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {PROGRAM_MODEL_LABELS[option]}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Активна"
          />
          {model === 'BONUS' ? (
            <TextField
              label="Задержка активации баллов (дней)"
              type="number"
              value={delayDays}
              onChange={(e) => setDelayDays(e.target.value)}
              error={delayDays.trim().length > 0 && !validDelay}
              fullWidth
              slotProps={{ htmlInput: { min: 0, step: 1 } }}
              helperText="0 — баллы доступны сразу"
            />
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending || !validDelay}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
